import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MagnifyingGlass, ArrowLeft, TrendUp, Trophy, Buildings, Users } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtQ = (n) => `Q${(Number(n) || 0).toLocaleString("es-GT", { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-GT", { year: "numeric", month: "short", day: "numeric" }) : "—";

export default function SuppliersPage() {
  const [query, setQuery] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);  // { supplier, awards }
  const [initialLoaded, setInitialLoaded] = useState(false);

  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await api.get(`/suppliers/search`, { params: { q, limit: 30 } });
      setSuppliers(res.data.suppliers || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Inteligencia de Proveedores requiere un plan activo");
      } else {
        toast.error(err.response?.data?.detail || "Error al buscar proveedores");
      }
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  }, []);

  useEffect(() => { search(""); }, [search]);

  const openDetail = async (nit) => {
    setLoading(true);
    try {
      const res = await api.get(`/suppliers/${encodeURIComponent(nit)}`);
      setSelected(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el proveedor");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); search(query.trim()); };

  // --- Detail view ---
  if (selected) {
    const s = selected.supplier;
    return (
      <div className="space-y-6" data-testid="supplier-detail-page">
        <Button
          variant="ghost" size="sm"
          onClick={() => setSelected(null)}
          className="rounded-none -ml-2"
          data-testid="supplier-back-btn"
        >
          <ArrowLeft size={16} className="mr-2" /> Volver a resultados
        </Button>

        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">NIT {s.nit}</p>
          <h1 className="font-heading font-black text-3xl tracking-tight leading-tight" data-testid="supplier-name">
            {s.name || "Proveedor sin nombre registrado"}
          </h1>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="rounded-none"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground mb-2"><Trophy size={14}/> Adjudicaciones</div>
            <p className="text-3xl font-heading font-black" data-testid="stat-awards-count">{s.awards_count}</p>
          </CardContent></Card>
          <Card className="rounded-none"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground mb-2"><TrendUp size={14}/> Total adjudicado</div>
            <p className="text-3xl font-heading font-black" data-testid="stat-total-amount">{fmtQ(s.total_amount)}</p>
          </CardContent></Card>
          <Card className="rounded-none"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground mb-2"><Users size={14}/> Participaciones</div>
            <p className="text-3xl font-heading font-black" data-testid="stat-participations">{s.participations_count}</p>
          </CardContent></Card>
          <Card className="rounded-none"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground mb-2"><Buildings size={14}/> Tasa de éxito</div>
            <p className="text-3xl font-heading font-black" data-testid="stat-success-rate">{s.success_rate}%</p>
          </CardContent></Card>
        </div>

        {/* Awards */}
        <div>
          <h2 className="text-sm font-mono uppercase tracking-wider text-muted-foreground mb-3">Adjudicaciones ({selected.awards.length})</h2>
          <Card className="rounded-none">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-mono text-xs uppercase">Fecha</TableHead>
                  <TableHead className="font-mono text-xs uppercase">NOG</TableHead>
                  <TableHead className="font-mono text-xs uppercase">Concurso</TableHead>
                  <TableHead className="font-mono text-xs uppercase">Entidad</TableHead>
                  <TableHead className="font-mono text-xs uppercase text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selected.awards.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">Este proveedor ha participado pero no tiene adjudicaciones ganadas registradas.</TableCell></TableRow>
                ) : selected.awards.map((a) => (
                  <TableRow key={`${a.nog}-${a.award_id}`} data-testid={`award-row-${a.nog}`}>
                    <TableCell className="text-xs font-mono">{fmtDate(a.award_date)}</TableCell>
                    <TableCell><a href={a.source_url} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline text-xs font-mono">{a.nog}</a></TableCell>
                    <TableCell className="text-sm max-w-md truncate" title={a.tender_title}>{a.tender_title}</TableCell>
                    <TableCell className="text-sm">{a.buyer_name}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtQ(a.award_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>
    );
  }

  // --- List view ---
  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight">Inteligencia de Proveedores</h1>
        <p className="text-sm text-muted-foreground font-mono mt-1">Historial de adjudicaciones, competidores y tasa de éxito</p>
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2 max-w-2xl">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="supplier-search-input"
            className="rounded-none pl-9 h-11 bg-card"
            placeholder="Buscar por NIT o razón social..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <Button type="submit" className="rounded-none bg-[#002FA7] hover:bg-[#001D66] h-11 px-6" disabled={loading} data-testid="supplier-search-btn">
          {loading ? "..." : "Buscar"}
        </Button>
      </form>

      <Card className="rounded-none" data-testid="suppliers-results">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-mono text-xs uppercase">NIT</TableHead>
              <TableHead className="font-mono text-xs uppercase">Razón social</TableHead>
              <TableHead className="font-mono text-xs uppercase text-right">Adjudicaciones</TableHead>
              <TableHead className="font-mono text-xs uppercase text-right">Total</TableHead>
              <TableHead className="font-mono text-xs uppercase">Última adjudicación</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && !initialLoaded ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Cargando...</TableCell></TableRow>
            ) : suppliers.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Sin resultados. Prueba otro NIT o parte del nombre.</TableCell></TableRow>
            ) : suppliers.map((s) => (
              <TableRow key={s.nit} className="cursor-pointer hover:bg-secondary/40" onClick={() => openDetail(s.nit)} data-testid={`supplier-row-${s.nit}`}>
                <TableCell className="font-mono text-xs">{s.nit}</TableCell>
                <TableCell className="text-sm font-medium">{s.name || "—"}</TableCell>
                <TableCell className="text-right"><Badge className="rounded-none font-mono text-[10px] bg-[#002FA7] text-white">{s.awards_count}</Badge></TableCell>
                <TableCell className="text-right font-mono text-sm">{fmtQ(s.total_amount)}</TableCell>
                <TableCell className="text-xs font-mono">{fmtDate(s.last_award_date)}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" className="rounded-none h-8 text-xs" data-testid={`open-supplier-${s.nit}`}>Ver perfil →</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
